import { UseCreationStatusResult } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-creation-status";
import { ReturnTypeEnum, returnTypeToTitle } from "@redotech/redo-model/return";
import {
  ReturnCreationActivity,
  Status,
} from "@redotech/redo-model/returns/temporal-status";
import CheckCircleFilled from "@redotech/redo-web/arbiter-icon/check-circle_filled.svg";
import ClockFilled from "@redotech/redo-web/arbiter-icon/clock_filled.svg";
import Loading from "@redotech/redo-web/arbiter-icon/progress-50.svg";
import XCircleFilled from "@redotech/redo-web/arbiter-icon/x-close_filled.svg";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { Text } from "@redotech/redo-web/text";
import { assertNever, isDefined } from "@redotech/util/type";
import { memo } from "react";

// If the display name is undefined, we are not showing this activity to the shopper
const getActivityToDisplayNameOrUndefined = (
  typeText: string,
): Record<ReturnCreationActivity, string | undefined> => {
  return {
    // Part 1: Parallel execution
    [ReturnCreationActivity.INCREMENT_RETURN_SUBMITTED_ACTIVITY]: `Tracking ${typeText}s submitted`,
    [ReturnCreationActivity.TRIGGER_CUSTOMER_EVENT_ACTIVITY]:
      "Triggering notifications",
    [ReturnCreationActivity.POPULATE_TIMELINE_ACTIVITY]: "Updating timeline",
    [ReturnCreationActivity.CREATE_PROACTIVE_SHIPPING_CLAIM_ACTIVITY]:
      "Creating proactive shipping claim",
    [ReturnCreationActivity.RETURN_CREATION_EMAIL_ACTIVITY]:
      "Sending confirmation email",
    // Sequential execution
    [ReturnCreationActivity.CREATE_EXCHANGE_ACTIVITY]:
      "Processing exchange items",
    [ReturnCreationActivity.CREATE_REPAIR_INVOICE_ACTIVITY]:
      "Checking repair invoice",
    [ReturnCreationActivity.CREATE_LABEL_ACTIVITY]: "Generating shipping label",
    // Part 2: RMA integrations (parallel)
    [ReturnCreationActivity.CREATE_RMA_ACTIVITY]: `Creating ${typeText} request`,
    [ReturnCreationActivity.CREATE_RMA_VIA_CUSTOM_INTEGRATION_ACTIVITY]:
      "Notifying integrations",
    [ReturnCreationActivity.CREATE_INMAR_RMA_ACTIVITY]: `Updating ${typeText} request with 3PL`,
    [ReturnCreationActivity.CREATE_BLUEYONDER_RMA_ACTIVITY]: `Creating BlueYonder ${typeText}`,
    // Sequential execution continues
    [ReturnCreationActivity.EMAIL_IN_STORE_RETURN_ACTIVITY]: `Checking in-store ${typeText}`,
    [ReturnCreationActivity.CREATE_SHOPIFY_RETURN_ACTIVITY]:
      "Updating platform records",
    [ReturnCreationActivity.UPDATE_BIGCOMMERCE_ORDER_ACTIVITY]:
      "Talking to third party",
    [ReturnCreationActivity.UPDATE_SHOP_APP_METAFIELDS_ACTIVITY]:
      "Updating metafields",
    [ReturnCreationActivity.UPDATE_RETURN_WITH_SHIPPING_REFUND_ACTIVITY]:
      "Check shipping information",
    // Part 3: Final parallel execution
    [ReturnCreationActivity.REPORT_CLAIM_TO_PROVIDER_ACTIVITY]:
      "Reporting to provider",
    [ReturnCreationActivity.CREATE_UPSELL_CHARGE_ACTIVITY]:
      "Creating upsell charge",
    [ReturnCreationActivity.TRIGGER_STATUS_CHANGE_ACTIVITY]: `Finalizing ${typeText} status`,
    [ReturnCreationActivity.NOTIFY_AND_TRIGGER_STATUS_CHANGE_ACTIVITY]: `Finalizing ${typeText} status`,
    [ReturnCreationActivity.VERIFY_ORDER_SCHEMA_ACTIVITY]: "Verifying schema",
    // Conditionally run activities (excluded from creation status)
    [ReturnCreationActivity.EMAIL_LABEL_ACTIVITY]: "Sending label email",
    [ReturnCreationActivity.REFUND_LABEL_ACTIVITY]: "Checking label",
    [ReturnCreationActivity.MARK_GIFT_RECEIPT_AS_USED_ACTIVITY]:
      "Checking receipts",
    [ReturnCreationActivity.COLLECT_POS_PAYMENT_ACTIVITY]:
      "Collecting POS payment",
    [ReturnCreationActivity.TAG_ORIGINAL_ORDER_ACTIVITY]:
      "Tagging original order",
    // Merchant notifications
    [ReturnCreationActivity.SEND_CLAIM_WARRANTY_NOTIFICATION_EMAIL_ACTIVITY]:
      undefined,
    [ReturnCreationActivity.SEND_RETURN_NOTIFICATION_EMAIL_ACTIVITY]: undefined,
    [ReturnCreationActivity.SEND_NEEDS_REVIEW_NOTIFICATION_EMAIL_ACTIVITY]:
      undefined,
    [ReturnCreationActivity.SEND_DROPSHIP_NOTIFICATION_EMAIL_ACTIVITY]:
      undefined,
    [ReturnCreationActivity.SEND_EXCHANGE_NOTIFICATION_EMAIL_ACTIVITY]:
      undefined,
    [ReturnCreationActivity.SEND_RETURN_TRACKING_NOTIFICATION_ACTIVITY]:
      undefined,
    [ReturnCreationActivity.POS_TRIGGER_PROCESSING_ACTIVITY]: undefined,
  };
};

export const DetailedCreationStatus = memo(function DetailedCreationStatus({
  creationStatus,
  returnType = ReturnTypeEnum.RETURN,
}: {
  creationStatus: UseCreationStatusResult["creationStatus"];
  returnType?: ReturnTypeEnum;
}): JSX.Element {
  if (!creationStatus?.activityStatuses) {
    return <Flex />;
  }

  const typeText = returnTypeToTitle[returnType].toLowerCase();

  const activityToDisplayName = getActivityToDisplayNameOrUndefined(typeText);

  function getStatusIcon(status: Status) {
    switch (status) {
      case Status.COMPLETED:
        return (props: any) => CheckCircleFilled(props);
      case Status.IN_PROGRESS:
        return (props: any) => Loading(props);
      case Status.FAILED:
        return (props: any) => XCircleFilled(props);
      case Status.NOT_STARTED:
        return (props: any) => ClockFilled(props);
      default:
        assertNever(status);
    }
  }

  function getStatusColor(status: Status) {
    switch (status) {
      case Status.COMPLETED:
        return "success";
      case Status.IN_PROGRESS:
        return "warning";
      case Status.FAILED:
        return "error";
      case Status.NOT_STARTED:
        return "light";
      default:
        assertNever(status);
    }
  }

  return (
    <Flex flexDirection="column" gap="lg" p="lg">
      <Text fontSize="lg" fontWeight="semibold" textAlign="center">
        Your {typeText} creation is taking longer than expected.
      </Text>
      <Text color="text-secondary" textAlign="center">
        Please wait while we process your {typeText}. This may take a few
        moments.
      </Text>

      <Flex flexDirection="column" pt="md">
        {creationStatus.activityStatuses.map((activity, index) => {
          const isInProgress = activity.status === Status.IN_PROGRESS;
          const isFailed = activity.status === Status.FAILED;
          const isCompleted = activity.status === Status.COMPLETED;
          const activityDisplayNameOrUndefined =
            activityToDisplayName[activity.name];

          if (!isDefined(activityDisplayNameOrUndefined)) {
            return null;
          }
          return (
            <Flex flexDirection="row" gap="sm" key={activity.name}>
              <Flex align="center" flexDirection="column">
                <Flex flexDirection="column">
                  <Icon
                    arbiterIconSvg={getStatusIcon(activity.status)}
                    color={getStatusColor(activity.status)}
                    size="compact"
                  />
                </Flex>
                {index < creationStatus.activityStatuses.length - 1 && (
                  <Flex
                    flex={1}
                    flexDirection="column"
                    pb="xxs"
                    pt="xxs"
                    style={{
                      borderColor: isCompleted ? "#22C55E" : "#E5E5E5",
                      borderWidth: 1,
                      borderRadius: 12,
                      borderStyle: "solid",
                      width: "2px",
                    }}
                  />
                )}
              </Flex>

              <Flex flex={1} flexDirection="column" pb="sm">
                <Text
                  color={
                    isFailed
                      ? "text-error"
                      : isCompleted
                        ? "text-success"
                        : "text-primary"
                  }
                  fontSize="md"
                  fontWeight={isInProgress ? "semibold" : "medium"}
                >
                  {activityDisplayNameOrUndefined}
                </Text>
                {activity.error && (
                  <Text color="text-error" fontSize="sm">
                    Error: {activity.error}
                  </Text>
                )}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
});
